/**
 * This file defines the `Directory` component for the React application, utilizing Firebase for data management.
 *
 * Author: Meng Li
 * Created on: 2024-09-30
 * Professor: Mejdi Eraslan
 * Client: Melisa Eraslan
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Dropdown, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../css/Directory.css';
import { db } from '../../firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';
import { FaExternalLinkAlt } from "react-icons/fa";
import FilterModal from '../components/FilterModal';

const Directory = () => {
    const [entities, setEntities] = useState([]);
    const [filteredEntities, setFilteredEntities] = useState([]);
    const [typeFilter, setTypeFilter] = useState('All');
    const [specificationFilter, setSpecificationFilter] = useState('All');
    const [sortField, setSortField] = useState('distance');
    const [sortDirection, setSortDirection] = useState('asc');
    const [userPosition, setUserPosition] = useState(null);
    const navigate = useNavigate();
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [activeFilters, setActiveFilters] = useState({});

    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Earth's radius in km
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a =
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return (R * c).toFixed(1);
    };

    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserPosition([
                        position.coords.latitude,
                        position.coords.longitude
                    ]);
                },
                (error) => {
                    console.error("Error getting location:", error);
                }
            );
        }
    }, []);

    const fetchData = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'entities'));
            const data = querySnapshot.docs
                .map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }))
                .filter(entity =>
                    entity.entity_name.toLowerCase() !== 'simplicity' &&
                    entity.entity_type !== 'Website Admin' &&
                    entity.entity_type !== 'SimpliCITYAdmin'
                );

            setEntities(data);
            setFilteredEntities(data);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleTypeFilterChange = (event) => {
        const type = event.target.value;
        setTypeFilter(type);
        applyFilters(type, specificationFilter);
    };

    const handleSpecificationFilterChange = (event) => {
        const specification = event.target.value;
        setSpecificationFilter(specification);
        applyFilters(typeFilter, specification);
    };

    const applyFilters = (type, specification) => {
        let filtered = [...entities];

        // Filter by entity type
        if (type !== 'All') {
            filtered = filtered.filter(entity => entity.entity_type === type);
        }

        // Filter by specifications keyword
        if (specification !== 'All') {
            const keyword = specification === 'gender neutral' ? 'gender' : specification;

            filtered = filtered.filter(entity => {
                const specs = entity.specifications?.toLowerCase() || '';

                // For men, ensure it doesn't contain "women" in the specifications
                if (specification === 'men') {
                    return specs.includes('men') && !specs.includes('women');
                }

                return specs.includes(keyword.toLowerCase());
            });
        }

        setFilteredEntities(filtered);
    };

    const handleSort = (field) => {
        if (field === sortField) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // 首先通过 filter 过滤
    const computedFilteredEntities = useMemo(() => {
        if (!activeFilters || Object.keys(activeFilters).length === 0) {
            return entities;
        }
        return entities.filter(entity => {
            // 获取第一个服务对象进行过滤
            const service = entity.services[0];
            if (!service) return false;

            // 服务时间过滤
            if (activeFilters.serviceHours?.length > 0) {
                const entityHours = service.hours_of_operation?.map(h => h.category) || [];
                if (!activeFilters.serviceHours.some(hour => entityHours.includes(hour))) {
                    return false;
                }
            }

            // 语言过滤
            if (activeFilters.languages?.length > 0) {
                if (!activeFilters.languages.some(lang =>
                    service.languages?.includes(lang)
                )) {
                    return false;
                }
            }

            // 资源过滤
            if (activeFilters.hasAvailableBeds && !service.resources?.beds?.available) {
                return false;
            }
            if (activeFilters.hasFood && !service.resources?.food?.available) {
                return false;
            }
            if (activeFilters.hasWellness && !service.resources?.wellness?.available) {
                return false;
            }
            if (activeFilters.hasOtherResources && !service.resources?.other_resources?.available) {
                return false;
            }

            // 健康资源过滤
            if (activeFilters.healthResources?.available && !service.resources?.health_resources?.available) {
                return false;
            }
            if (activeFilters.healthResources?.requiresOHIP && !service.resources?.health_resources?.requires_ohip) {
                return false;
            }
            if (activeFilters.healthResources?.requiresID && !service.resources?.health_resources?.requires_id) {
                return false;
            }

            // 心理健康支持过滤
            if (activeFilters.mentalHealthSupport?.available && !service.resources?.mental_health_support?.available) {
                return false;
            }
            if (activeFilters.mentalHealthSupport?.requiresOHIP && !service.resources?.mental_health_support?.requires_ohip) {
                return false;
            }
            if (activeFilters.mentalHealthSupport?.requiresID && !service.resources?.mental_health_support?.requires_id) {
                return false;
            }

            // 性别要求过
            if (activeFilters.gender?.length > 0) {
                if (!activeFilters.gender.some(gender =>
                    service.eligibility?.gender?.includes(gender)
                )) {
                    return false;
                }
            }

            // 年龄要求过滤
            if (activeFilters.age?.length > 0) {
                if (!activeFilters.age.some(age =>
                    service.eligibility?.age?.includes(age)
                )) {
                    return false;
                }
            }

            // 群体政策过滤
            if (activeFilters.allowsFamilies && !service.eligibility?.groups?.families) {
                return false;
            }
            if (activeFilters.allowsImmigrants && !service.eligibility?.groups?.immigrants) {
                return false;
            }
            if (activeFilters.allowsLGBTQ && !service.eligibility?.groups?.lgbtq_plus) {
                return false;
            }
            if (activeFilters.allowsPets && !service.eligibility?.groups?.allows_pets) {
                return false;
            }
            if (activeFilters.allowsDrugs && !service.eligibility?.drug_policy?.allowed) {
                return false;
            }
            if (activeFilters.allowsAlcohol && !service.eligibility?.alcohol_policy?.allowed) {
                return false;
            }

            // 要求过滤
            if (activeFilters.hasFees && !service.fees?.required) {
                return false;
            }
            if (activeFilters.requiresDocuments && !service.documents_required?.required) {
                return false;
            }
            if (activeFilters.requiresApplication && !service.application_required?.required) {
                return false;
            }

            // 无障碍设施过滤
            if (activeFilters.isFullyAccessible && !service.accessibility?.fully_accessible) {
                return false;
            }

            return true;
        });
    }, [entities, activeFilters]);

    // 然后对过滤后的结果进行排序
    useEffect(() => {
        if (computedFilteredEntities.length > 0) {
            const sorted = [...computedFilteredEntities].sort((a, b) => {
                if (sortField === 'distance' && userPosition) {
                    const distanceA = calculateDistance(
                        userPosition[0],
                        userPosition[1],
                        a.address.latitude,
                        a.address.longitude
                    );
                    const distanceB = calculateDistance(
                        userPosition[0],
                        userPosition[1],
                        b.address.latitude,
                        b.address.longitude
                    );
                    return sortDirection === 'asc' ? distanceA - distanceB : distanceB - distanceA;
                } else if (sortField === 'entity_name') {
                    return sortDirection === 'asc'
                        ? a.entity_name.localeCompare(b.entity_name)
                        : b.entity_name.localeCompare(a.entity_name);
                }
                return 0;
            });
            setFilteredEntities(sorted);
        } else {
            setFilteredEntities([]); // 如果没有过滤结果，设置为空数组
        }
    }, [sortField, sortDirection, userPosition, computedFilteredEntities]);

    const handleEntityClick = (id) => {
        navigate(`/entityInfo?entityId=${id}`);
    };

    const handleFilterClick = () => {
        setShowFilterModal(true);
    };

    const handleFilterApply = (filters) => {
        setActiveFilters(filters);
        setShowFilterModal(false);
    };

    return (
        <Container className="EntityDirectory-page">
            <Row className="my-4">
                <Col xs={12} className="d-flex gap-3 align-items-center">
                    <button
                        className="filter-button"
                        onClick={handleFilterClick}
                    >
                        Filter
                    </button>

                    <Dropdown>
                        <Dropdown.Toggle className="filter-button" id="dropdown-basic">
                            Sort by
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item
                                onClick={() => handleSort('entity_name')}
                                active={sortField === 'entity_name'}
                            >
                                Entity Name
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => handleSort('distance')}
                                active={sortField === 'distance'}
                            >
                                Distance
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                </Col>
            </Row>
            <Row>
                {filteredEntities.length === 0 ? (
                    <Col className="text-center">
                        <p>No matching entities found</p>
                    </Col>
                ) : (
                    filteredEntities.map((entity, index) => (
                        <Col key={index} xs={12} md={6} className="mb-4">
                            <div className="entity-card">
                                <div className="left">
                                    <h5 onClick={() => handleEntityClick(entity.id)} className="entity-name">
                                        {entity.entity_name}
                                    </h5>
                                    {entity.services?.map((service, idx) => (
                                        service.sub_title && (
                                            <p key={idx} className="sub-title">
                                                {service.sub_title}
                                            </p>
                                        )
                                    ))}
                                    {entity.official_website && (
                                        <a className="links" href={entity.official_website} target="_blank" rel="noopener noreferrer">
                                            Official Website
                                            <FaExternalLinkAlt />
                                        </a>
                                    )}
                                </div>
                                <div className="right">
                                    <p className="entity-type">{entity.entity_type}</p>
                                    <div className="address-container">
                                        <p>
                                            {entity.address?.address_line_1}
                                            {entity.address?.address_line_2 ? `, ${entity.address.address_line_2}` : ''}
                                        </p>
                                        <p>{entity.address?.city}, {entity.address?.postal_code}</p>
                                    </div>
                                    {userPosition && entity.address?.latitude && entity.address?.longitude && (
                                        <p className="distance">
                                            {calculateDistance(
                                                userPosition[0],
                                                userPosition[1],
                                                entity.address.latitude,
                                                entity.address.longitude
                                            )} km
                                        </p>
                                    )}
                                </div>
                            </div>
                        </Col>
                    ))
                )}
            </Row>

            {/* 添加 FilterModal 组件 */}
            {showFilterModal && (
                <FilterModal
                    isOpen={showFilterModal}
                    onClose={() => setShowFilterModal(false)}
                    onApply={handleFilterApply}
                />
            )}
        </Container>
    );
};

export default Directory;
